import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Tooltip, Typography } from "@mui/material"
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from 'axios';
import { AttachementsUpload, ButtonLoader } from "../../../shared_elements/components";
import FilePreview from "../../../shared_elements/components/FilePreview";
import fileUpload, { formatBytes } from "../../../utils/fileUpload";
import { trackActivity } from "../../../utils/mixpanel";
import { enqueueSnackbar } from "notistack";
import { DeletePopUp, PageLoader } from "../../../shared_elements";
import { getStorage } from "../../../shared_elements/actionCreators";
import { globalDeleteService } from "../../../utils/globalApiServices";
import { checkApiStatus } from "../../../utils";
import { PermissionWrapper } from "../../../wrappers";
import DeleteIcon from '@mui/icons-material/Delete';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import { Visibility as VisibilityIcon } from '@mui/icons-material';

const UploadAttachmentsModal = ({ uploadAttachments, onToggleModal, getResponseBack, invSlug, progressData, onAttachmentDeleted, permissionKey = 'invoices', onViewDisabled }) => {
    const dispatch = useDispatch()
    const { storageDetails, permission } = useSelector((state) => state.leases);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isLoading, setLoading] = useState({ pageLoader: false, storage: false });
    const [existingAttachments, setExistingAttachments] = useState(uploadAttachments?.data?.attachments || []);
    const [deleteAttachment, setDeleteAttachment] = useState({ modal: false, data: null });
    const [filePreviewData, setFilePreviewData] = useState({ modal: false, data: null });
    const [expiredLink, setExpiredLink] = useState({});
    const [isDeleting, setDeleting] = useState(false);

    useEffect(() => {
        dispatch(getStorage({ lease_slug: uploadAttachments?.data?.lease_slug, module_name: "invoices", setIsDropDownLoading: setLoading }))
    }, [])

    useEffect(() => {
        setExistingAttachments(uploadAttachments?.data?.attachments || []);
    }, [uploadAttachments?.data?.attachments])

    useEffect(() => {
        if (!existingAttachments.length) return;
        const interval = setInterval(() => {
            setExpiredLink(
                Object.fromEntries(existingAttachments.map(item => [item.slug, isUrlExpired(item.download_url)]))
            );
        }, 1000);
        return () => clearInterval(interval);
    }, [existingAttachments]);

    const isUrlExpired = (downloadUrl) => {
        const urlObj = new URL(downloadUrl);
        const amzDate = urlObj.searchParams.get("X-Amz-Date");
        const expires = Number(urlObj.searchParams.get("X-Amz-Expires"));
        if (!amzDate || !expires) return true;
        const createdAt = new Date(amzDate.replace(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/, "$1-$2-$3T$4:$5:$6Z"));
        return Date.now() > createdAt.getTime() + expires * 1000;
    }

    const handleFileChanges = (files) => {
        setUploadedFiles(files);
    }
    const handleUpload = async (slug, mode) => {
        invSlug(slug)
        setLoading({ ...isLoading, pageLoader: true });
        setTimeout(() => { setLoading({ ...isLoading, pageLoader: false }); onToggleModal(); }, 1500);
        await fileUpload({
            attachments: uploadedFiles,
            module_name: "invoices",
            slug: slug,
            query: { record_action_type: mode },
            onProgress: ({ progressRefs, attachments, response }) => {
                progressData({ progressRefs, attachments, response });
            },
            onSuccess: (success_msg) => {
                enqueueSnackbar(success_msg, {
                    variant: "success",
                    anchorOrigin: { vertical: "top", horizontal: "right" }
                });
                getResponseBack();
            },
            onError: (err) => {
                enqueueSnackbar(`${err?.message}` || "Error while uploading files", {
                    variant: "error",
                    anchorOrigin: { vertical: "top", horizontal: "right" }
                });
            }
        });
        setUploadedFiles([]);
        invSlug(null)
    };
    const onDownloadAttachment = async (file_url, fileName) => {
        setDeleting(true);
        axios.get(file_url, { responseType: "arraybuffer" })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", fileName);
                document.body.appendChild(link);
                link.click();
                setDeleting(false);
            })
            .catch(() => {
                enqueueSnackbar("The download link has expired. Please reload the page.", {
                    variant: "error",
                    anchorOrigin: { vertical: "top", horizontal: "right" }
                });
                setDeleting(false);
            })
    };
    const onDeleteAttachment = () => {
        setDeleting(true);
        globalDeleteService(`delete-attachment/${deleteAttachment?.data?.slug}`).then(response => {
            if (checkApiStatus(response)) {
                setDeleting(false);
                enqueueSnackbar(response.data.message, { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
                setExistingAttachments(existingAttachments.filter(item => item.slug !== deleteAttachment?.data?.slug));
                onAttachmentDeleted?.(deleteAttachment?.data?.slug);
                setDeleteAttachment({ modal: false, data: null });
                dispatch(getStorage({ lease_slug: uploadAttachments?.data?.lease_slug, module_name: "invoices", setIsDropDownLoading: setLoading }))
            } else {
                setDeleting(false);
                enqueueSnackbar(response.data.message, { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
            }
        })
    }
    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: `Add Invoice Attachments`, page_type: page_type, })
    }
    const uploadOperationPermission = permission?.permissions?.lease_revamp_application?.["lease_revamp"]?.[permissionKey];
    const isUploadSectionVisible = ['U'].some(element => uploadOperationPermission?.includes(element));
    return (
        <>
            <Dialog open={uploadAttachments?.modal}
                className="add-lease-dialog"
                maxWidth="sm"
                fullWidth >
                <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title" className='dialog-header'> Invoice Attachments
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} >
                        {existingAttachments.length ?
                            <Grid size={{ md: 12, xs: 12 }}>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#5f5f5f', mb: '6px' }}>
                                    {`Attachment(s) (${existingAttachments.length})`}
                                </Typography>
                                <Box sx={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                                    {existingAttachments.map((item, index) => {
                                        const isExpired = expiredLink[item.slug];
                                        return (
                                            <Box key={index} className="flex-centered" sx={{ justifyContent: 'space-between', flexWrap: 'nowrap', borderBottom: index !== existingAttachments.length - 1 ? '1px solid #efefef' : 'none', padding: '6px 10px' }}>
                                                <Box className="flex-centered" sx={{ display:'flex', flexShrink: 0, flexWrap: 'nowrap' }}>
                                                    {/* <PermissionWrapper extraProps={{ application: "lease_revamp", feature: permissionKey, operationType: ['U'] }}> */}
                                                        <Tooltip title={isExpired ? "Link Expired" : "Download"} arrow>
                                                            <span>
                                                                <VerticalAlignBottomIcon
                                                                    fontSize="small"
                                                                    color={isExpired ? "disabled" : "primary"}
                                                                    sx={{ cursor: isExpired ? "not-allowed" : "pointer", mr: '6px' }}
                                                                    onClick={() => { if (!isExpired) { onDownloadAttachment(item?.download_url, item?.file_name); } else { enqueueSnackbar("The download link has expired. Please reload the page.", { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" } }); } handleItemClicked(`Download Icon (${item?.file_name})`) }}
                                                                />
                                                            </span>
                                                        </Tooltip>
                                                    {/* </PermissionWrapper> */}
                                                    <Tooltip title="View" arrow>
                                                        <VisibilityIcon
                                                            color="primary"
                                                            fontSize="small"
                                                            sx={{ cursor: 'pointer', mr: onViewDisabled ? 0 : '6px' }}
                                                            onClick={() => { setFilePreviewData({ modal: true, data: item }); handleItemClicked(`View Icon (${item?.file_name})`) }}
                                                        />
                                                    </Tooltip>
                                                    {onViewDisabled ? null :
                                                        <PermissionWrapper extraProps={{ application: "lease_revamp", feature: permissionKey, operationType: ['D'] }}>
                                                            <Tooltip title="Delete" arrow>
                                                                <DeleteIcon
                                                                    fontSize='small'
                                                                    color='error'
                                                                    sx={{ cursor: 'pointer' }}
                                                                    onClick={() => { setDeleteAttachment({ modal: true, data: item }); handleItemClicked(`Delete Icon (${item?.file_name})`) }}
                                                                />
                                                            </Tooltip>
                                                        </PermissionWrapper>
                                                    }
                                                    <Typography sx={{ fontSize: '13px', wordBreak: 'break-all', pl: '8px', flex: 1, minWidth: 0 }}>{item?.file_name}</Typography>
                                                </Box>
                                            </Box>
                                        )
                                    })}
                                </Box>
                            </Grid> : null
                        }
                        <PermissionWrapper extraProps={{ application: "lease_revamp", feature: permissionKey, operationType: ['U'] }}>
                            <Grid size={{ md: 12, xs: 12 }}>
                                {existingAttachments.length ? <Divider sx={{ mb: '12px' }} /> : null}
                                <Box className="responsive-buttons">
                                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#5f5f5f' }}>Upload New Attachment(s)</Typography>
                                    <Button className='available-space-btn' disableRipple size='small' variant='outlined' color='primary' endIcon={isLoading?.storage ? <ButtonLoader size={15} /> : null}>{`Available Space : ${isLoading?.storage ? "" : formatBytes(storageDetails?.remaining_storage)}`}</Button>
                                </Box>
                                <AttachementsUpload onFileChange={handleFileChanges} attachments={existingAttachments} remainingStorage={storageDetails?.remaining_storage} isDisabled={uploadAttachments?.data?.is_summary_invoice} />
                            </Grid>
                        </PermissionWrapper>
                        <Grid size={{ md: 12, xs: 12 }}>
                            {!existingAttachments.length && !isUploadSectionVisible && <Typography>No Attachments Found</Typography>}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button className="cancel-btn" variant="outlined" size="small" onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }}>{!uploadedFiles.length ? "Close" : "Cancel"}</Button>
                    {!uploadedFiles.length ? null : <Button disabled={!uploadedFiles.length} variant="contained" className="btn-primary" size="small" onClick={() => { handleUpload(uploadAttachments?.data?.slug, 'updated'); handleItemClicked('Save Button') }}>SAVE</Button>}
                </DialogActions>
            </Dialog>
            {deleteAttachment.modal ?
                <DeletePopUp
                    modal={deleteAttachment.modal}
                    title='Delete Attachment'
                    content={`Are you sure, you want to Delete?`}
                    onToggleModal={() => setDeleteAttachment({ modal: false, data: null })}
                    onDeleteRecords={onDeleteAttachment}
                /> : null
            }
            {filePreviewData?.modal ?
                <FilePreview
                    filePreview={filePreviewData}
                    onToggleModal={() => setFilePreviewData({ modal: false, data: null })}
                /> : null}
            {isLoading?.pageLoader || isDeleting ? <PageLoader /> : null}
        </>
    )
}
export default UploadAttachmentsModal;
